import { useEffect, useState } from "react";
import {
  fetchAcademyFaqs,
  createAcademyFaq,
  deleteAcademyFaq,
} from "@/api/academyAuth";

export default function Faqs() {
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState({
    question: "",
    answer: "",
    order: 0,
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedFaqs, setExpandedFaqs] = useState({});
  const [isFormVisible, setIsFormVisible] = useState(false);

  // تحميل FAQ الأكاديمية
  const loadFaqs = async () => {
    try {
      const data = await fetchAcademyFaqs();
      // ترتيب FAQ حسب حقل order
      const sortedFaqs = [...(data || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
      setFaqs(sortedFaqs);
    } catch (e) {
      console.error(e);
      setError("فشل تحميل الأسئلة الشائعة");
    }
  };

  useEffect(() => {
    loadFaqs();
  }, []);

  // إضافة FAQ
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      await createAcademyFaq({
        question: form.question.trim(),
        answer: form.answer.trim(),
        order: Number(form.order) || 0,
      });

      setForm({
        question: "",
        answer: "",
        order: 0,
      });
      
      setSuccess("تم إضافة السؤال الشائع بنجاح");
      loadFaqs();
      
      // إخفاء النموذج بعد الإضافة الناجحة
      setTimeout(() => {
        setIsFormVisible(false);
        setSuccess("");
      }, 2000);
    } catch (e) {
      setError("فشل إضافة السؤال. يرجى المحاولة مرة أخرى.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // حذف FAQ
  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا السؤال؟")) return;

    try {
      await deleteAcademyFaq(id);
      setSuccess("تم حذف السؤال بنجاح");
      loadFaqs();
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError("فشل حذف السؤال");
      console.error(e);
    }
  };

  // تبديل حالة توسيع/طي السؤال
  const toggleFaq = (id) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // تنسيقات CSS
  const styles = {
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "25px 20px",
      minHeight: "calc(100vh - 160px)"
    },
    header: {
      marginBottom: "30px",
      paddingBottom: "20px",
      borderBottom: "2px solid #e9ecef"
    },
    title: {
      margin: "0 0 10px 0",
      color: "#182848",
      fontSize: "28px",
      fontWeight: "700",
      display: "flex",
      alignItems: "center",
      gap: "12px"
    },
    subtitle: {
      margin: "0 0 20px 0",
      color: "#6c757d",
      fontSize: "16px"
    },
    statsCard: {
      backgroundColor: "white",
      borderRadius: "12px",
      padding: "20px",
      boxShadow: "0 3px 10px rgba(0, 0, 0, 0.08)",
      display: "flex",
      alignItems: "center",
      gap: "15px",
      marginBottom: "30px"
    },
    statIcon: {
      width: "50px",
      height: "50px",
      borderRadius: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: "0",
      backgroundColor: "rgba(75, 108, 183, 0.1)"
    },
    statContent: {
      flex: "1"
    },
    statTitle: {
      margin: "0 0 5px 0",
      color: "#6c757d",
      fontSize: "14px",
      fontWeight: "500"
    },
    statValue: {
      margin: "0",
      color: "#182848",
      fontSize: "24px",
      fontWeight: "700"
    },
    actionBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "25px",
      flexWrap: "wrap",
      gap: "15px"
    },
    toggleButton: {
      padding: "12px 25px",
      background: "linear-gradient(90deg, #4b6cb7 0%, #182848 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontFamily: "inherit"
    },
    faqsCount: {
      color: "#182848",
      fontSize: "16px",
      fontWeight: "600",
      backgroundColor: "rgba(75, 108, 183, 0.1)",
      padding: "8px 16px",
      borderRadius: "20px"
    },
    formContainer: {
      backgroundColor: "white",
      borderRadius: "15px",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
      padding: "25px",
      marginBottom: "30px",
      display: isFormVisible ? "block" : "none",
      animation: "slideDown 0.3s ease-out"
    },
    formTitle: {
      margin: "0 0 25px 0",
      color: "#182848",
      fontSize: "20px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      paddingBottom: "15px",
      borderBottom: "1px solid #e9ecef"
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: "20px"
    },
    formGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px"
    },
    label: {
      color: "#495057",
      fontWeight: "500",
      fontSize: "15px",
      display: "flex",
      alignItems: "center",
      gap: "5px"
    },
    required: {
      color: "#dc3545"
    },
    input: {
      padding: "14px 16px",
      border: "1px solid #e1e5eb",
      borderRadius: "10px",
      fontSize: "16px",
      transition: "all 0.3s",
      backgroundColor: "#f8f9fa",
      boxSizing: "border-box",
      fontFamily: "inherit",
      width: "100%"
    },
    textarea: {
      padding: "14px 16px",
      border: "1px solid #e1e5eb",
      borderRadius: "10px",
      fontSize: "16px",
      transition: "all 0.3s",
      backgroundColor: "#f8f9fa",
      boxSizing: "border-box",
      fontFamily: "inherit",
      width: "100%",
      minHeight: "120px",
      resize: "vertical"
    },
    formButtons: {
      display: "flex",
      gap: "15px",
      marginTop: "10px"
    },
    submitButton: {
      padding: "14px 25px",
      background: "linear-gradient(90deg, #4b6cb7 0%, #182848 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      flex: "1",
      fontFamily: "inherit"
    },
    cancelButton: {
      padding: "14px 25px",
      backgroundColor: "#f8f9fa",
      color: "#495057",
      border: "1px solid #e1e5eb",
      borderRadius: "10px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      flex: "1",
      fontFamily: "inherit"
    },
    buttonDisabled: {
      opacity: "0.7",
      cursor: "not-allowed"
    },
    spinner: {
      width: "20px",
      height: "20px",
      border: "2px solid rgba(255, 255, 255, 0.3)",
      borderRadius: "50%",
      borderTopColor: "white",
      animation: "spin 1s ease-in-out infinite"
    },
    successMessage: {
      backgroundColor: "#d4edda",
      border: "1px solid #c3e6cb",
      color: "#155724",
      padding: "15px",
      borderRadius: "10px",
      marginTop: "15px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "15px",
      animation: "fadeIn 0.3s ease-out"
    },
    errorMessage: {
      backgroundColor: "#f8d7da",
      border: "1px solid #f5c6cb",
      color: "#721c24",
      padding: "15px",
      borderRadius: "10px",
      marginTop: "15px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "15px",
      animation: "fadeIn 0.3s ease-out"
    },
    icon: {
      width: "20px",
      height: "20px",
      flexShrink: "0"
    },
    faqsContainer: {
      backgroundColor: "white",
      borderRadius: "15px",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
      overflow: "hidden"
    },
    faqsList: {
      listStyle: "none",
      margin: "0",
      padding: "0"
    },
    faqItem: {
      borderBottom: "1px solid #e9ecef",
      transition: "all 0.3s"
    },
    faqHeader: {
      padding: "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      cursor: "pointer",
      backgroundColor: "white",
      transition: "background-color 0.2s"
    },
    faqContent: {
      padding: "0 20px",
      maxHeight: "0",
      overflow: "hidden",
      transition: "all 0.3s ease",
      backgroundColor: "#f8f9fa"
    },
    faqContentExpanded: {
      padding: "20px",
      maxHeight: "500px",
      borderTop: "1px solid #e9ecef"
    },
    faqQuestion: {
      margin: "0",
      color: "#182848",
      fontSize: "16px",
      fontWeight: "600",
      flex: "1",
      display: "flex",
      alignItems: "center",
      gap: "12px"
    },
    faqAnswer: {
      margin: "0",
      color: "#495057",
      fontSize: "15px",
      lineHeight: "1.6"
    },
    faqMeta: {
      display: "flex",
      alignItems: "center",
      gap: "15px",
      marginTop: "15px",
      paddingTop: "15px",
      borderTop: "1px solid #e9ecef"
    },
    orderBadge: {
      backgroundColor: "rgba(75, 108, 183, 0.1)",
      color: "#4b6cb7",
      padding: "4px 12px",
      borderRadius: "12px",
      fontSize: "13px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "5px"
    },
    deleteButton: {
      backgroundColor: "rgba(220, 53, 69, 0.1)",
      color: "#dc3545",
      border: "1px solid rgba(220, 53, 69, 0.3)",
      borderRadius: "6px",
      padding: "6px 12px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.3s"
    },
    toggleIcon: {
      width: "20px",
      height: "20px",
      transition: "transform 0.3s"
    },
    emptyState: {
      textAlign: "center",
      padding: "60px 20px",
      backgroundColor: "white",
      borderRadius: "15px",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)"
    },
    emptyIcon: {
      fontSize: "64px",
      color: "#adb5bd",
      marginBottom: "20px",
      opacity: "0.5"
    },
    emptyTitle: {
      margin: "0 0 10px 0",
      color: "#495057",
      fontSize: "24px",
      fontWeight: "600"
    },
    emptyText: {
      margin: "0 0 25px 0",
      color: "#6c757d",
      fontSize: "16px"
    }
  };

  // أيقونات SVG
  const FaqIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ ...styles.icon, color: "#4b6cb7", width: "28px", height: "28px" }}>
      <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 01-.988-1.129c1.454-1.272 3.776-1.272 5.23 0 1.513 1.324 1.513 3.518 0 4.842a3.75 3.75 0 01-.837.552c-.676.328-1.028.774-1.028 1.152v.75a.75.75 0 01-1.5 0v-.75c0-1.279 1.06-2.107 1.875-2.502.182-.088.351-.199.503-.331.83-.727.83-1.857 0-2.584zM12 18a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
    </svg>
  );

  const SuccessIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ ...styles.icon, color: "#155724" }}>
      <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
    </svg>
  );

  const ErrorIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ ...styles.icon, color: "#721c24" }}>
      <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
    </svg>
  );

  const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: "18px", height: "18px" }}>
      <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 9a.75.75 0 00-1.5 0v2.25H9a.75.75 0 000 1.5h2.25V15a.75.75 0 001.5 0v-2.25H15a.75.75 0 000-1.5h-2.25V9z" clipRule="evenodd" />
    </svg>
  );

  const ChevronDownIcon = ({ expanded }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ 
      ...styles.toggleIcon, 
      transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
      color: "#4b6cb7"
    }}>
      <path fillRule="evenodd" d="M12.53 16.28a.75.75 0 01-1.06 0l-7.5-7.5a.75.75 0 011.06-1.06L12 14.69l6.97-6.97a.75.75 0 111.06 1.06l-7.5 7.5z" clipRule="evenodd" />
    </svg>
  );

  const OrderIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: "14px", height: "14px" }}>
      <path fillRule="evenodd" d="M6.97 2.47a.75.75 0 011.06 0l4.5 4.5a.75.75 0 01-1.06 1.06L8.25 4.81V16.5a.75.75 0 01-1.5 0V4.81L3.53 8.03a.75.75 0 01-1.06-1.06l4.5-4.5zm9.53 4.28a.75.75 0 01.75.75v11.69l3.22-3.22a.75.75 0 111.06 1.06l-4.5 4.5a.75.75 0 01-1.06 0l-4.5-4.5a.75.75 0 111.06-1.06l3.22 3.22V7.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
    </svg>
  );

  // تأثيرات hover
  const handleInputFocus = (e) => {
    e.target.style.outline = "none";
    e.target.style.borderColor = "#4b6cb7";
    e.target.style.boxShadow = "0 0 0 3px rgba(75, 108, 183, 0.1)";
    e.target.style.backgroundColor = "white";
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = "#e1e5eb";
    e.target.style.boxShadow = "none";
    e.target.style.backgroundColor = "#f8f9fa";
  };

  const handleButtonHover = (e, type = "primary") => {
    if (type === "primary") {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "0 7px 14px rgba(75, 108, 183, 0.2)";
    } else if (type === "delete") {
      e.currentTarget.style.backgroundColor = "rgba(220, 53, 69, 0.2)";
      e.currentTarget.style.transform = "translateY(-1px)";
    } else if (type === "toggle") {
      e.currentTarget.style.transform = "translateY(-1px)";
    }
  };

  const handleButtonLeave = (e, type = "primary") => {
    if (type === "primary") {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    } else if (type === "delete") {
      e.currentTarget.style.backgroundColor = "rgba(220, 53, 69, 0.1)";
      e.currentTarget.style.transform = "translateY(0)";
    } else if (type === "toggle") {
      e.currentTarget.style.transform = "translateY(0)";
    }
  };

  const handleFaqHover = (e) => {
    e.currentTarget.style.backgroundColor = "#f8f9fa";
  };

  const handleFaqLeave = (e) => {
    e.currentTarget.style.backgroundColor = "white";
  };

  return (
    <div style={styles.container}>
      {/* رأس الصفحة */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaqIcon />
          الأسئلة الشائعة (FAQ)
        </h1>
        <p style={styles.subtitle}>إدارة الأسئلة الشائعة وإجاباتها في الأكاديمية</p>
      </div>

      {/* إحصائيات سريعة */}
      <div style={styles.statsCard}>
        <div style={styles.statIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#4b6cb7" style={{ width: "24px", height: "24px" }}>
            <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 01-.988-1.129c1.454-1.272 3.776-1.272 5.23 0 1.513 1.324 1.513 3.518 0 4.842a3.75 3.75 0 01-.837.552c-.676.328-1.028.774-1.028 1.152v.75a.75.75 0 01-1.5 0v-.75c0-1.279 1.06-2.107 1.875-2.502.182-.088.351-.199.503-.331.83-.727.83-1.857 0-2.584zM12 18a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
          </svg>
        </div>
        <div style={styles.statContent}>
          <h3 style={styles.statTitle}>إجمالي الأسئلة الشائعة</h3>
          <p style={styles.statValue}>{faqs.length}</p>
        </div>
      </div>

      {/* شريط الإجراءات */}
      <div style={styles.actionBar}>
        <button 
          style={styles.toggleButton}
          onClick={() => setIsFormVisible(!isFormVisible)}
          onMouseEnter={(e) => handleButtonHover(e, "toggle")}
          onMouseLeave={(e) => handleButtonLeave(e, "toggle")}
        >
          <PlusIcon />
          {isFormVisible ? "إخفاء نموذج الإضافة" : "إضافة سؤال جديد"}
        </button>
        
        <div style={styles.faqsCount}>
          {faqs.length} سؤال{faqs.length !== 1 ? 'اً' : ''}
        </div>
      </div>

      {/* نموذج إضافة/تعديل سؤال */}
      <div style={styles.formContainer}>
        <h2 style={styles.formTitle}>
          {isFormVisible ? "إضافة سؤال جديد" : ""}
        </h2>
        
        {isFormVisible && (
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                السؤال
                <span style={styles.required}>*</span>
              </label>
              <input
                placeholder="أدخل السؤال الشائع"
                value={form.question}
                onChange={(e) => setForm({ ...form, question: e.target.value })}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                required
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                الإجابة
                <span style={styles.required}>*</span>
              </label>
              <textarea
                placeholder="أدخل الإجابة التفصيلية"
                value={form.answer}
                onChange={(e) => setForm({ ...form, answer: e.target.value })}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                required
                style={styles.textarea}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                ترتيب العرض
              </label>
              <input
                type="number"
                placeholder="0"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                min="0"
                style={styles.input}
              />
              <small style={{ color: "#6c757d", fontSize: "13px" }}>
                الرقم الأقل يظهر أولاً
              </small>
            </div>

            <div style={styles.formButtons}>
              <button 
                type="submit" 
                style={{
                  ...styles.submitButton,
                  ...(loading ? styles.buttonDisabled : {})
                }}
                disabled={loading}
                onMouseEnter={(e) => handleButtonHover(e, "primary")}
                onMouseLeave={(e) => handleButtonLeave(e, "primary")}
              >
                {loading ? (
                  <>
                    <div style={styles.spinner}></div>
                    جاري الإضافة...
                  </>
                ) : (
                  "إضافة السؤال"
                )}
              </button>

              <button 
                type="button" 
                onClick={() => setIsFormVisible(false)}
                style={styles.cancelButton}
                onMouseEnter={(e) => handleButtonHover(e, "secondary")}
                onMouseLeave={(e) => handleButtonLeave(e, "secondary")}
              >
                إلغاء
              </button>
            </div>

            {/* رسائل النجاح والخطأ */}
            {success && (
              <div style={styles.successMessage}>
                <SuccessIcon />
                {success}
              </div>
            )}

            {error && (
              <div style={styles.errorMessage}>
                <ErrorIcon />
                {error}
              </div>
            )}
          </form>
        )}
      </div>

      {/* قائمة الأسئلة الشائعة */}
      <div style={styles.faqsContainer}>
        {faqs.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>❓</div>
            <h3 style={styles.emptyTitle}>لا توجد أسئلة شائعة</h3>
            <p style={styles.emptyText}>
              قم بإضافة أول سؤال شائع لتظهر هنا
            </p>
            <button 
              style={styles.toggleButton}
              onClick={() => setIsFormVisible(true)}
              onMouseEnter={(e) => handleButtonHover(e, "toggle")}
              onMouseLeave={(e) => handleButtonLeave(e, "toggle")}
            >
              <PlusIcon />
              إضافة أول سؤال
            </button>
          </div>
        ) : (
          <ul style={styles.faqsList}>
            {faqs.map((faq) => {
              const isExpanded = expandedFaqs[faq.id];
              return (
                <li 
                  key={faq.id} 
                  style={styles.faqItem}
                  onMouseEnter={handleFaqHover}
                  onMouseLeave={handleFaqLeave}
                >
                  <div 
                    style={styles.faqHeader}
                    onClick={() => toggleFaq(faq.id)}
                  >
                    <h3 style={styles.faqQuestion}>
                      <span style={{ 
                        backgroundColor: "#4b6cb7", 
                        color: "white", 
                        width: "28px", 
                        height: "28px", 
                        borderRadius: "50%", 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center", 
                        fontSize: "14px", 
                        fontWeight: "600" 
                      }}>
                        Q
                      </span>
                      {faq.question}
                    </h3>
                    <ChevronDownIcon expanded={isExpanded} />
                  </div>
                  
                  <div 
                    style={{
                      ...styles.faqContent,
                      ...(isExpanded ? styles.faqContentExpanded : {})
                    }}
                  >
                    <p style={styles.faqAnswer}>{faq.answer}</p>
                    
                    <div style={styles.faqMeta}>
                      <span style={styles.orderBadge}>
                        <OrderIcon />
                        الترتيب: {faq.order || 0}
                      </span>
                      
                      <button 
                        onClick={() => handleDelete(faq.id)}
                        style={styles.deleteButton}
                        onMouseEnter={(e) => handleButtonHover(e, "delete")}
                        onMouseLeave={(e) => handleButtonLeave(e, "delete")}
                      >
                        حذف السؤال
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* إضافة الأنيميشن */}
      <style>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
            max-height: 0;
          }
          to {
            opacity: 1;
            transform: translateY(0);
            max-height: 800px;
          }
        }
        
        .faq-item {
          animation: fadeIn 0.3s ease-out;
        }
        
        @media (max-width: 768px) {
          .action-bar {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          
          .toggle-button, .faqs-count {
            width: 100% !important;
            text-align: center !important;
          }
          
          .form-buttons {
            flex-direction: column !important;
          }
          
          .faq-header {
            padding: 15px !important;
          }
          
          .faq-question {
            font-size: 15px !important;
          }
        }
      `}</style>
    </div>
  );
}